<script setup>
import { useSeo } from '@/composables/useSeo'

useSeo('institutions')
import { onMounted } from 'vue'
import genImages from '@/generated/gen-images.json'
import {
  ServerStackIcon,
  LanguageIcon,
  IdentificationIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon
} from '@heroicons/vue/24/outline'

// Capabilities grid — copy lives in i18n (gov.capabilities.items), visuals here
const capabilityIcons = [
  ServerStackIcon,
  LanguageIcon,
  IdentificationIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon
]

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-reveal')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
    observer.observe(el)
  })
})
</script>

<template>
  <!-- ================= HERO ================= -->
  <header class="relative pt-40 pb-28 md:pb-32 overflow-hidden bg-slate-950 border-b border-white/10">
    <!-- Visuel de fond + dégradé de lisibilité -->
    <div class="absolute inset-0">
      <img :src="genImages['solutions-institutions-hero']" alt="" aria-hidden="true"
        class="w-full h-full object-cover opacity-80 brightness-110" />
      <div class="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/75 to-slate-950/10"></div>
      <div class="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-950 to-transparent"></div>
    </div>
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">
          {{ $t('gov.hero.label') }}
        </p>
        <h1 class="text-4xl md:text-6xl font-semibold text-white leading-[1.1]">
          {{ $t('gov.hero.title') }}
          <span class="text-primary-400">{{ $t('gov.hero.titleAccent') }}</span>
        </h1>
        <p class="mt-6 text-lg text-slate-300 leading-relaxed">
          {{ $t('gov.hero.desc') }}
        </p>
        <div class="mt-10 flex flex-col sm:flex-row gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition text-center">
            {{ $t('gov.hero.ctaDemo') }}
          </a>
          <a href="#capabilities"
            class="px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition text-center">
            {{ $t('gov.hero.ctaSecondary') }}
          </a>
        </div>
      </div>
    </div>
  </header>

  <!-- ================= CAPABILITIES ================= -->
  <section id="capabilities" class="py-28 bg-slate-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">
          {{ $t('gov.capabilities.label') }}
        </p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">
          {{ $t('gov.capabilities.title') }}
        </h2>
        <p class="text-lg text-slate-500">{{ $t('gov.capabilities.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="(icon, i) in capabilityIcons" :key="i"
          class="group bg-white p-8 hover:bg-slate-50 transition-colors duration-200">
          <div class="flex items-start justify-between mb-6">
            <component :is="icon" class="w-7 h-7 text-slate-900" />
            <span class="text-[11px] font-mono text-slate-400 border border-slate-200 rounded px-2 py-0.5">
              {{ String(i + 1).padStart(2, '0') }}
            </span>
          </div>
          <h3 class="text-lg font-semibold text-slate-900 mb-2">
            {{ $t(`gov.capabilities.items[${i}].title`) }}
          </h3>
          <p class="text-sm text-slate-500 leading-relaxed">
            {{ $t(`gov.capabilities.items[${i}].desc`) }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= SHOWCASE — écran d'affichage officiel ================= -->
  <section class="bg-slate-950">
    <div class="relative overflow-hidden aspect-[3/1] reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
      <img :src="genImages['institutions-display']"
        alt="Grand écran d'affichage dans une salle institutionnelle montrant une carte et des données lumineuses abstraites"
        class="w-full h-full object-cover" loading="lazy" />
      <div class="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50"></div>
    </div>
  </section>

  <!-- ================= SOVEREIGNTY ================= -->
  <section class="py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">

        <div class="lg:col-span-2 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">
            {{ $t('gov.data.label') }}
          </p>
          <h2 class="text-4xl md:text-5xl font-semibold mb-5">{{ $t('gov.data.title') }}</h2>
          <p class="text-lg text-slate-400 leading-relaxed">{{ $t('gov.data.desc') }}</p>
        </div>

        <div
          class="lg:col-span-3 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <div v-for="i in 4" :key="i" class="bg-slate-950 p-8">
            <div class="text-[11px] font-mono text-primary-400 mb-4">{{ String(i).padStart(2, '0') }}</div>
            <h3 class="text-lg font-semibold text-white mb-2">
              {{ $t(`gov.data.items[${i - 1}].title`) }}
            </h3>
            <p class="text-sm text-slate-400 leading-relaxed">
              {{ $t(`gov.data.items[${i - 1}].desc`) }}
            </p>
          </div>
        </div>

      </div>
    </div>
  </section>

  <!-- ================= CTA ================= -->
  <section class="py-24 bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="bg-slate-950 rounded-lg p-12 md:p-20 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <h2 class="text-3xl md:text-5xl font-semibold text-white mb-6">{{ $t('gov.cta.title') }}</h2>
        <p class="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">{{ $t('gov.cta.desc') }}</p>
        <a href="https://bifrost.events/touch" target="_blank"
          class="inline-block px-7 py-3.5 bg-white text-slate-950 font-semibold rounded-md text-base hover:bg-slate-200 transition">
          {{ $t('gov.cta.btn') }}
        </a>
      </div>
    </div>
  </section>
</template>

<style>
</style>
